/**
 * BR OS Tax Reserves in the Property Portfolio layout (Fan, 2026-10-27:
 * "do it for Tax Reserves as well under BR OS scope").
 *
 * Input is GET /bros/tax-reserves/country-months (all countries); the
 * backend already computed every figure per country, currency and month.
 * This only adds rows up, converting to GBP with the app's /fx/rates
 * (labelled approx in the UI). An amount with no rate is skipped and
 * counted in `unconverted`, never treated as 1.0.
 */
import { makeToGbp, MONTH_LABELS } from './taxOverview';

export const COUNTRY_INFO = {
  DE: { label: 'Germany', flag: '\uD83C\uDDE9\uD83C\uDDEA' },
  IE: { label: 'Ireland', flag: '\uD83C\uDDEE\uD83C\uDDEA' },
  NL: { label: 'Netherlands', flag: '\uD83C\uDDF3\uD83C\uDDF1' },
  DK: { label: 'Denmark', flag: '\uD83C\uDDE9\uD83C\uDDF0' },
  FR: { label: 'France', flag: '\uD83C\uDDEB\uD83C\uDDF7' },
  UK: { label: 'UK', flag: '\uD83C\uDDEC\uD83C\uDDE7' },
  BELGIUM: { label: 'Belgium', flag: '\uD83C\uDDE7\uD83C\uDDEA' },
  FINLAND: { label: 'Finland', flag: '\uD83C\uDDEB\uD83C\uDDEE' },
  PORTUGAL: { label: 'Portugal', flag: '\uD83C\uDDF5\uD83C\uDDF9' },
};

export function countryLabel(code) {
  const c = COUNTRY_INFO[String(code || '').toUpperCase()];
  if (c) return `${c.flag} ${c.label}`;
  const s = String(code || 'Unknown');
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export const BROS_FIELDS = [
  'cash_received', 'supplier_cost', 'vat_payable', 'tax', 'profit_after_tax', 'profit_share', 'working_capital',
];

export function buildBrosOverview({ countryMonths, fx, country = 'all', year }) {
  const toGbp = makeToGbp(fx);
  let unconverted = 0;
  const conv = (v, ccy) => {
    if (!Number(v)) return 0;
    const g = toGbp(v, ccy);
    if (g === null) { unconverted += 1; return 0; }
    return g;
  };
  const rows = (countryMonths?.months || []).filter((m) => country === 'all' || m.country === country);

  const blank = () => {
    const o = { payments: 0, unresolved: 0 };
    BROS_FIELDS.forEach((f) => { o[f] = 0; });
    return o;
  };
  const months = MONTH_LABELS.map((label, i) => ({ ...blank(), label, month: `${year}-${String(i + 1).padStart(2, '0')}` }));
  const kpis = blank();
  const byCountry = {};

  rows.forEach((m) => {
    const idx = Number(String(m.month).slice(5, 7)) - 1;
    const inYear = String(m.month).slice(0, 4) === String(year) && idx >= 0 && idx < 12;
    const key = `${m.country}|${m.currency}`;
    if (!byCountry[key]) byCountry[key] = { country: m.country, currency: m.currency, native: blank(), gbp: blank() };
    const bc = byCountry[key];
    BROS_FIELDS.forEach((f) => {
      const native = Number(m[f]) || 0;
      const g = conv(native, m.currency);
      bc.native[f] += native;
      bc.gbp[f] += g;
      kpis[f] += g;
      if (inYear) months[idx][f] += g;
    });
    const p = Number(m.payments) || 0;
    const u = Number(m.unresolved_payments) || 0;
    bc.native.payments += p; bc.native.unresolved += u;
    kpis.payments += p; kpis.unresolved += u;
    if (inYear) { months[idx].payments += p; months[idx].unresolved += u; }
  });

  const countries = Object.values(byCountry).sort((a, b) => b.gbp.cash_received - a.gbp.cash_received);
  const totalsRow = blank();
  months.forEach((m) => {
    BROS_FIELDS.forEach((f) => { totalsRow[f] += m[f]; });
    totalsRow.payments += m.payments;
    totalsRow.unresolved += m.unresolved;
  });
  return { kpis, months, totalsRow, countries, unconverted };
}

/** Pill options: All plus every country with cash, biggest first. */
export function countryOptions(countryMonths, fx) {
  const toGbp = makeToGbp(fx);
  const tot = {};
  (countryMonths?.months || []).forEach((m) => {
    tot[m.country] = (tot[m.country] || 0) + (toGbp(m.cash_received, m.currency) || 0);
  });
  return [{ key: 'all', label: 'All' }].concat(
    Object.entries(tot).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([c]) => ({ key: c, label: countryLabel(c) }))
  );
}
